import React, { useState, useEffect } from 'react';
import { securityApi } from '../services/api';
import { ShieldCheck, Key } from '@phosphor-icons/react';

export default function SecurityQuestionModal({ onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [selectedQ, setSelectedQ] = useState('');
  const [answer, setAnswer] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    securityApi.getQuestions()
      .then(r => setQuestions(r.data.questions || []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedQ) { setError('Please select a security question'); return; }
    if (answer.trim().length < 2) { setError('Answer must be at least 2 characters'); return; }
    setSaving(true);
    try {
      await securityApi.setQuestion({ question: selectedQ, answer: answer.trim() });
      if (onComplete) onComplete();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save. Please try again.');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="security-question-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-2xl shadow-2xl border p-6 space-y-5"
        style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--card-border)' }}>

        <div className="text-center">
          <div className="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ backgroundColor: 'var(--primary-light, #EFF6FF)' }}>
            <ShieldCheck className="w-7 h-7" style={{ color: 'var(--primary)' }} weight="fill" />
          </div>
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-heading)' }}>
            Set Up Security Question
          </h2>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
            This is required for your account security. It will be used to recover your password if you forget it.
          </p>
        </div>

        {error && (
          <div className="p-3 rounded-lg text-sm bg-red-50 text-red-600 border border-red-200" data-testid="sqm-error">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="sqm-form">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-heading)' }}>
              <Key className="w-3.5 h-3.5 inline mr-1" />
              Security Question
            </label>
            <select
              value={selectedQ}
              onChange={(e) => setSelectedQ(e.target.value)}
              required
              className="w-full px-3 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border)', color: 'var(--text-heading)' }}
              data-testid="sqm-select"
            >
              <option value="">-- Select a question --</option>
              {questions.map((q, i) => <option key={i} value={q}>{q}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-heading)' }}>Your Answer</label>
            <input
              type="text"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              required
              minLength={2}
              placeholder="Type your answer (case-insensitive)"
              className="w-full px-3 py-2.5 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
              style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--border)', color: 'var(--text-heading)' }}
              data-testid="sqm-answer"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--primary)' }}
            data-testid="sqm-save-btn"
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        </form>

        <p className="text-[10px] text-center" style={{ color: 'var(--text-muted)' }}>
          You can update your security question anytime from Settings.
        </p>
      </div>
    </div>
  );
}
